// src/components/AddNewProductPage.tsx
import React, { useState, ChangeEvent, FormEvent, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
    Save, Loader2, Image as ImageIcon, ChevronRight, 
    Package, Layers, ArrowLeft, CheckCircle, AlertCircle, X, UploadCloud
} from 'lucide-react';

// --- IMPORTANT URLS ---
const SPRING_API_URL = '/springboot'; // Spring Boot (Products)
const NODE_API_URL = '/nodejs';   // Node.js (Categories)

// --- TOAST COMPONENT ---
const Toast = ({ show, message, type, onClose }: any) => {
    if (!show) return null;
    const isSuccess = type === 'success';
    return (
        <div className={`fixed top-6 left-1/2 transform -translate-x-1/2 z-[110] flex items-center gap-3 px-5 py-3 rounded-lg shadow-xl border animate-in slide-in-from-top-5 fade-in duration-300 ${isSuccess ? 'bg-white border-green-200 text-green-800' : 'bg-white border-red-200 text-red-800'}`}>
            <div className={`p-1.5 rounded-full ${isSuccess ? 'bg-green-100' : 'bg-red-100'}`}>
                {isSuccess ? <CheckCircle size={18} className="text-green-600"/> : <AlertCircle size={18} className="text-red-600"/>}
            </div>
            <div>
                <h4 className="text-sm font-bold">{isSuccess ? 'Success' : 'Error'}</h4>
                <p className="text-xs font-medium opacity-90">{message}</p>
            </div>
            <button onClick={onClose} className="ml-4 p-1 hover:bg-black/5 rounded-full"><X size={14}/></button>
        </div>
    );
};

interface Category {
  id: number;
  name: string;
  subCategories?: Category[];
}

const AddNewProductPage: React.FC = () => {
    const navigate = useNavigate();
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Form States
    const [productName, setProductName] = useState('');
    const [description, setDescription] = useState('');
    const [price, setPrice] = useState('');
    const [minQuantity, setMinQuantity] = useState('');
    const [quantityUnit, setQuantityUnit] = useState('');
    const [hsnSku, setHsnSku] = useState('');
    const [youtubeUrl, setYoutubeUrl] = useState('');
    
    // Category Logic
    const [selectedCategoryPath, setSelectedCategoryPath] = useState<number[]>([]);
    const [nestedCategories, setNestedCategories] = useState<Category[]>([]);
    
    // Image Logic
    const [selectedImages, setSelectedImages] = useState<File[]>([]);
    const [previewUrls, setPreviewUrls] = useState<string[]>([]);

    // Status
    const [loading, setLoading] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

    const showToast = (message: string, type: 'success' | 'error' = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
    };

    // 1. Fetch Categories
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await fetch(`${NODE_API_URL}/api/categories`);
                if (!response.ok) throw new Error('Failed to fetch categories');
                const data = await response.json();
                setNestedCategories(data);
            } catch (err) {
                showToast('Error connecting to category service.', 'error');
            }
        };
        fetchCategories();
    }, []);

    // 2. Handle Multiple Images
    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const filesArray = Array.from(e.target.files);
            setSelectedImages(prev => [...prev, ...filesArray]);

            const newPreviews = filesArray.map(file => URL.createObjectURL(file));
            setPreviewUrls(prev => [...prev, ...newPreviews]);
        }
    };

    const removeImage = (index: number) => {
        setSelectedImages(prev => prev.filter((_, i) => i !== index));
        setPreviewUrls(prev => prev.filter((_, i) => i !== index));
    };

    // 3. Handle Category Selection
    const handleCategoryChange = (e: ChangeEvent<HTMLSelectElement>, level: number) => {
        const catId = parseInt(e.target.value);
        const newPath = selectedCategoryPath.slice(0, level);
        if (catId) newPath.push(catId);
        setSelectedCategoryPath(newPath);
    };

    // 4. Submit Form
    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setLoading(true);

        // FIX: Changed from 'sellerToken' to 'token' to match other pages
        const token = localStorage.getItem('sellerToken'); 
        if (!token) {
            showToast('Please login as a seller first.', 'error');
            setLoading(false);
            return;
        }

        if (selectedCategoryPath.length === 0) {
            showToast('Please select a category.', 'error');
            setLoading(false);
            return;
        }

        const finalCategoryId = selectedCategoryPath[selectedCategoryPath.length - 1];

        const formData = new FormData();
        formData.append('productName', productName);
        formData.append('description', description);
        formData.append('price', price);
        formData.append('minimumOrderQuantity', minQuantity);
        formData.append('unit', quantityUnit);
        formData.append('hsnSku', hsnSku);
        formData.append('categoryId', finalCategoryId.toString());
        if (youtubeUrl) formData.append('youtubeUrl', youtubeUrl);
        
        selectedImages.forEach((file) => {
            formData.append('imageFiles', file);
        });

        try {
            const response = await fetch(`${SPRING_API_URL}/api/seller/products`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            if (!response.ok) {
                throw new Error("Failed to add product");
            }

            showToast('Product Added Successfully!');
            setTimeout(() => navigate('/seller/product'), 1500);

        } catch (err: any) {
            showToast(err.message || 'Error adding product', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Recursive Category Dropdowns
    let currentCats = nestedCategories;
    const dropDowns = [];
    for (let i = 0; i <= selectedCategoryPath.length; i++) {
        if (!currentCats || currentCats.length === 0) break;
        const val = selectedCategoryPath[i] || '';
        
        dropDowns.push(
            <div key={i} className="mb-4">
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                    {i === 0 ? 'Main Category' : `Sub Category Level ${i}`}
                </label>
                <div className="relative">
                    <select
                        value={val}
                        onChange={(e) => handleCategoryChange(e, i)}
                        className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-[#00635e] outline-none bg-white"
                    >
                        <option value="">Select Category</option>
                        {currentCats.map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </select>
                </div>
            </div>
        );

        if (val) {
            const selectedCat = currentCats.find(c => c.id === val);
            currentCats = selectedCat?.subCategories || [];
        } else {
            break;
        }
    }

    return (
        <div className="bg-gray-50/50 min-h-screen font-sans pb-20">
            
            <Toast show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />

            {/* HEADER */}
            <div className="bg-white border-b border-gray-200 sticky top-0 z-20 shadow-sm">
                <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-4">
                    <Link to="/seller/product" className="p-2 hover:bg-gray-100 rounded-full text-gray-500 transition">
                        <ArrowLeft size={20} />
                    </Link>
                    <div>
                        <h1 className="text-xl font-black text-[#00635e] flex items-center gap-2">
                            Add New Product
                        </h1>
                        <p className="text-xs text-gray-500">List a new item in your inventory.</p>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto p-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                    
                    {/* 1. BASIC INFO */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                        <h3 className="text-sm font-bold text-[#00635e] flex items-center mb-5 border-b pb-2">
                            <Package size={18} className="mr-2 text-red-600"/> Basic Information
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1">PRODUCT NAME *</label>
                                <input className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-1 focus:ring-[#00635e] outline-none" 
                                    value={productName} onChange={e => setProductName(e.target.value)} required placeholder="e.g. Industrial Valve" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1">PRICE (INR) *</label>
                                <input className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-1 focus:ring-[#00635e] outline-none" 
                                    type="number" value={price} onChange={e => setPrice(e.target.value)} required placeholder="0.00" />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-xs font-bold text-gray-700 mb-1">DESCRIPTION *</label>
                                <textarea className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-1 focus:ring-[#00635e] outline-none h-28 resize-none" 
                                    value={description} onChange={e => setDescription(e.target.value)} required placeholder="Enter product details, specifications etc." />
                            </div>
                        </div>
                    </div>

                    {/* 2. INVENTORY & SPECS */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                        <h3 className="text-sm font-bold text-[#00635e] flex items-center mb-5 border-b pb-2">
                            <Layers size={18} className="mr-2 text-red-600"/> Inventory & Specifications
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1">MIN ORDER QTY *</label>
                                <input className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-1 focus:ring-[#00635e] outline-none" 
                                    type="number" value={minQuantity} onChange={e => setMinQuantity(e.target.value)} required placeholder="e.g. 10" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1">UNIT (e.g. Kg, Pcs) *</label>
                                <input className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-1 focus:ring-[#00635e] outline-none" 
                                    value={quantityUnit} onChange={e => setQuantityUnit(e.target.value)} required placeholder="e.g. Pieces" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1">HSN/SKU CODE</label>
                                <input className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-1 focus:ring-[#00635e] outline-none" 
                                    value={hsnSku} onChange={e => setHsnSku(e.target.value)} placeholder="Optional" />
                            </div>
                        </div>
                    </div>

                    {/* 3. CATEGORY */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                        <h3 className="text-sm font-bold text-[#00635e] flex items-center mb-5 border-b pb-2">
                            <ChevronRight size={18} className="mr-2 text-red-600"/> Category Classification
                        </h3>
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                            {dropDowns}
                        </div>
                    </div>

                    {/* 4. IMAGES */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                        <h3 className="text-sm font-bold text-[#00635e] flex items-center mb-5 border-b pb-2">
                            <ImageIcon size={18} className="mr-2 text-red-600"/> Product Images
                        </h3>
                        
                        <div className="space-y-4">
                            <div 
                                onClick={() => fileInputRef.current?.click()}
                                className="border-2 border-dashed border-gray-300 rounded-xl p-8 flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 hover:border-[#00635e] transition-all group"
                            >
                                <div className="bg-teal-50 p-3 rounded-full mb-3 group-hover:scale-110 transition-transform">
                                    <UploadCloud className="text-[#00635e]" size={24} />
                                </div>
                                <p className="text-sm font-medium text-gray-700">Click to upload images</p>
                                <p className="text-xs text-gray-400 mt-1">PNG, JPG up to 5MB</p>
                                <input 
                                    type="file" 
                                    accept="image/*" 
                                    multiple 
                                    onChange={handleFileChange} 
                                    ref={fileInputRef} 
                                    className="hidden" 
                                />
                            </div>
                            
                            {/* Preview Grid */}
                            {previewUrls.length > 0 && (
                                <div className="grid grid-cols-3 md:grid-cols-6 gap-4 mt-4">
                                    {previewUrls.map((url, index) => (
                                        <div key={index} className="relative group h-24 w-24 rounded-lg overflow-hidden border border-gray-200 shadow-sm">
                                            <img src={url} alt={`Preview ${index}`} className="h-full w-full object-cover" />
                                            <button 
                                                type="button"
                                                onClick={() => removeImage(index)}
                                                className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
                                            >
                                                <X size={12} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* SUBMIT BUTTON */}
                    <div className="flex justify-end pt-4">
                        <button type="submit" disabled={loading} className="flex items-center gap-2 bg-red-600 text-white px-8 py-3 rounded-xl font-bold hover:bg-red-700 shadow-lg shadow-red-200 transition active:scale-95 disabled:opacity-70 disabled:scale-100">
                            {loading ? <Loader2 className="animate-spin" size={20} /> : <Save size={20} />}
                            {loading ? 'Adding Product...' : 'Submit Product'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddNewProductPage;